import { NamePath } from 'antd/lib/form/interface';
import dayjs from 'dayjs';
import { ParsedUrlQuery } from 'querystring';
import { option } from '../../components/CustomFieldComponent';
import { CustomFieldsValueType, TicketFormValues } from '../../components/GrispiTicketForm';
import { defaultConversationVisibilityType } from '../../containers/Pages/AdminPage/CompanySettingsView';
import {
    AuxGrispiFormItemsName,
    IFieldDefinition,
    IForm,
    NewTicket,
    TicketSystemFieldsKey,
    commentTypeOptionsMap,
    customFieldOptionTypes,
    customFieldOptionsMap
} from '../../model/types';
import { nonNullishValue } from '../helpers';
import {
    addAppPrefixToStorageKey,
    getSessionStorageItem,
    removeSessionStorageItem,
    setSessionStorageItem
} from '../storageHelpers';
import { getFieldInstanceFromTicket } from '../ticketHelper';
import {
    getFieldKey,
    hasArrayTypeValueFeature,
    isAssigneeTypeField,
    isDateTimeTypeField,
    isSelectTypeField,
    isValueArrayTypeField
} from './customFieldsHelper';
import {
    interpolateDateTimeValue,
    interpolateSerializedValueForAssigneeField,
    interpolateSerializedValueForFieldHasArrayTypeValue,
    interpolateUserFriendlyValueForDistributionListToField
} from './formFieldsHelper.values';
import { RequesterEditFormValues } from './requesterEditFormHelper';

// this method provides to continue on ticket creation or ticket update process where the user left off
// ! refactored
export const persistTicketFormValues =
    <T extends TicketFormValues>(formName: string) =>
    (formValues?: T): void => {
        if (!formName) {
            // console.debug('persistTicketFormValues: formName is empty');
            return;
        }
        if (formName.includes('preview-form')) return;

        let newValues = formValues;

        const currentPersistedFormValues = getFormValuesOnStorage<T>(formName);
        if (currentPersistedFormValues) {
            const { fields: currentPersistedFormFields, ...restCurrentPersistedFormValues } =
                currentPersistedFormValues;
            const { fields: newValuesFields, ...restNewValues } = newValues;

            newValues = {
                ...restCurrentPersistedFormValues,
                ...restNewValues,
                fields: { ...currentPersistedFormFields, ...newValuesFields }
            } as T;
        }

        setSessionStorageItem(addAppPrefixToStorageKey(formName), newValues);
    };

// ! refactored
// after selecting the another ticket tab, this method prevents to lose UI-friendly label on antd-inputs those have built-in multiple selection feature
export const persistFormOptions = (itemKey: string, option: option[]): void => {
    if (itemKey.includes('preview-form')) return;
    setSessionStorageItem(itemKey, option);
};

// ! refactored
export const getFormValuesOnStorage = <T>(formName: string): T => {
    if (!formName) {
        // console.debug('getFormValuesOnStorage: formName is empty');
        return null;
    }
    return getSessionStorageItem(addAppPrefixToStorageKey(formName));
};

// ! refactored
export const getFormAttachments = (formName: string): number[] =>
    getSessionStorageItem(storageItemKeyFactoryForAttachments(formName))
        ?.filter(({ id }) => id)
        .map(({ id }) => id) ?? [];

// ! refactored
export const storageItemKeyFactoryForAttachments = (formName: string): string => {
    if (!formName) {
        // console.debug('storageItemKeyFactoryForAttachments: formName is empty');
        return null;
    }
    return addAppPrefixToStorageKey(`${formName}-attachmentIds`);
};

export const interpolateDateTimeFieldsPersistedValues = <T extends TicketFormValues>(
    persistedFormValues: T,
    ticketFieldList: IFieldDefinition[]
): T => {
    if (!persistedFormValues?.fields) {
        // console.debug('interpolateDateTimeFieldsPersistedValues: persistedFormValues.fields is empty');
        return persistedFormValues;
    }

    const dateTimeFieldKeyList = ticketFieldList?.filter(isDateTimeTypeField)?.map(getFieldKey) ?? [];

    Object.keys(persistedFormValues.fields)?.forEach((fieldKey) => {
        if (dateTimeFieldKeyList.includes(fieldKey)) {
            persistedFormValues.fields[fieldKey] = interpolateDateTimeValue(
                persistedFormValues.fields[fieldKey] as string
            );
        }
    });

    return persistedFormValues;
};

// ! refactored
// change values as milliseconds from dayjs object before sending the request
const interpolateDateTimeFieldsPostValues = <T extends TicketFormValues>(
    formValues: T,
    ticketFieldList: IFieldDefinition[]
): T => {
    const dateTimeFieldKeyList = ticketFieldList?.filter(isDateTimeTypeField)?.map(getFieldKey) ?? [];

    Object.keys(formValues.fields)?.forEach((fieldKey) => {
        if (dateTimeFieldKeyList.includes(fieldKey) && formValues.fields?.[fieldKey]) {
            formValues.fields[fieldKey] = new Date(formValues.fields[fieldKey] as string).getTime();
        }
    });

    return formValues;
};
// ! refactored
const interpolateMultipleOptionsAvailableFieldsPostValues = <T extends TicketFormValues>(
    formValues: T,
    ticketFieldList: IFieldDefinition[]
): T => {
    const multipleOptionsAvailableFieldKeyList = ticketFieldList?.filter(isValueArrayTypeField)?.map(getFieldKey) ?? [];

    Object.keys(formValues.fields)?.forEach((fieldKey) => {
        if (multipleOptionsAvailableFieldKeyList.includes(fieldKey)) {
            formValues.fields[fieldKey] = formValues.fields[fieldKey] ?? [];
        }
    });

    return formValues;
};

const interpolateNullishFieldPostValues = <T extends TicketFormValues>(formValues: T): T => {
    Object.keys(formValues.fields)?.forEach((fieldKey) => {
        if (!formValues.fields[fieldKey]) {
            formValues.fields[fieldKey] = null;
        }
    });

    return formValues;
};

// ! refactored
export const interpolateTicketFormValues = <T extends TicketFormValues>(
    formValues: T,
    ticketFieldList: IFieldDefinition[]
): T => {
    if (!formValues?.fields) {
        // console.debug('interpolateTicketFormValues: formValues.fields is empty');
        return formValues;
    }

    interpolateDateTimeFieldsPostValues(formValues, ticketFieldList);
    interpolateMultipleOptionsAvailableFieldsPostValues(formValues, ticketFieldList);
    interpolateNullishFieldPostValues(formValues);
    return formValues;
};

export const doesFormIdMatch =
    (formId: number) =>
    (form: IForm): boolean =>
        form?.id === formId;

export const generateTicketFormName = (pathname: string, query: ParsedUrlQuery): string => {
    if (pathname.startsWith('/requests')) return 'end-user-request-form';

    const trimmedUrl = pathname.split('[')[0];
    if (query?.no) return `${trimmedUrl}${query?.no}`;
    if (query?.ticketKey) return `${trimmedUrl}${query?.ticketKey}`;
};

type formMode = 'create' | 'edit';
export const defineMode = (urlString: string): formMode => (urlString.includes('/new') ? 'create' : 'edit');

export const removeFormRelatedStorageItems = (formName: string): void => {
    const storageItemKeyList = Object.keys(window.sessionStorage).filter((storageItemKey) =>
        storageItemKey.includes(formName)
    );
    storageItemKeyList.forEach((storageItemKey) => {
        removeSessionStorageItem(storageItemKey);
    });
};

export enum FormSystemFields {
    name = 'fields'
}

export const constructFormValuesObject =
    (namePath: NamePath) =>
    <T>(value: T): Record<string, Record<string, T>> | Record<string, T> =>
        Array.isArray(namePath)
            ? {
                  [namePath[0]]: { [namePath[1]]: value }
              }
            : {
                  [namePath]: value
              };

export const getDirtyInputs = <T extends RequesterEditFormValues | RequesterEditFormValues['fields']>(
    curValues: T,
    initialValues: T
): Partial<T> => {
    const dirtyInputs = Object.entries(curValues).reduce((acc, [key, value]) => {
        if (key === FormSystemFields.name && Object.keys(curValues[key]).length > 0) {
            const dirtyFields = getDirtyInputs(curValues[key][value], initialValues[key]);
            return { ...acc, [key]: dirtyFields };
        }

        const isNeededStringifyCheck = Array.isArray(value);

        const isIt = isNeededStringifyCheck
            ? stringifyCheck(value, initialValues?.[key])
            : (value ? (dayjs.isDayjs(value) ? value.toISOString() : value) : '') !== (initialValues?.[key] ?? '');

        return isIt ? { ...acc, [key]: value } : { ...acc };
    }, {});

    return dirtyInputs;
};

// ! refactored
export const defaultCommentVisibilityDefinerMap = {
    PUBLIC: commentTypeOptionsMap.public,
    PRIVATE: commentTypeOptionsMap.internal
} as const;

export const populateInitialValues =
    (activeFields: IFieldDefinition[]) =>
    (ticketDetail: NewTicket) =>
    (defaultCommentVisibility: defaultConversationVisibilityType): TicketFormValues => {
        if (!activeFields) {
            // console.debug('populateInitialValues: activeFields is null');
            return {} as TicketFormValues;
        }

        let distributionListTo: string = null;
        const fields = activeFields.reduce((acc, field) => {
            const { key: fieldKey } = field;

            const fieldInstance = getFieldInstanceFromTicket(ticketDetail)(fieldKey);

            if (isDateTimeTypeField(field)) {
                return {
                    ...acc,
                    [fieldKey]: interpolateDateTimeValue(fieldInstance?.userFriendlyValue)
                };
            }

            if (fieldKey === TicketSystemFieldsKey.requester) {
                distributionListTo = interpolateUserFriendlyValueForDistributionListToField(
                    fieldInstance?.userFriendlyValue
                );
            }

            const serializedValue = fieldInstance?.serializedValue;

            if (isAssigneeTypeField(field)) {
                return {
                    ...acc,
                    [fieldKey]: interpolateSerializedValueForAssigneeField(serializedValue)
                };
            }

            if ((isSelectTypeField(field) && hasArrayTypeValueFeature(field)) || isValueArrayTypeField(field)) {
                return { ...acc, [fieldKey]: interpolateSerializedValueForFieldHasArrayTypeValue(serializedValue) };
            }

            return { ...acc, [fieldKey]: serializedValue ?? null };
        }, {});

        if (!defaultCommentVisibility) {
            // console.debug('Default comment visibility is empty, will set to PUBLIC explicitly');
            defaultCommentVisibility = 'PUBLIC';
        }

        return {
            [AuxGrispiFormItemsName.channel]: ticketDetail?.channel,
            [AuxGrispiFormItemsName.comment]: null,
            [AuxGrispiFormItemsName.commentType]: defaultCommentVisibilityDefinerMap[defaultCommentVisibility],
            [AuxGrispiFormItemsName.distributionListTo]: distributionListTo,
            [AuxGrispiFormItemsName.activeForm]: ticketDetail?.form?.id,
            [FormSystemFields.name]: fields
        } as TicketFormValues;
    };

export const isMultipleOptionAvailableField = (type: customFieldOptionTypes): boolean =>
    [
        customFieldOptionsMap.MULTI_USER,
        // customFieldOptionsMap.ASSIGNEE,
        customFieldOptionsMap.MULTI_SELECT,
        customFieldOptionsMap.TAG
    ].includes(type as any);

// ! refactored
const stringifyCheck = (
    value: string | number | (string | number)[],
    initialValue: string | number | (string | number)[]
): boolean => JSON.stringify(value) !== JSON.stringify(initialValue);

// ! refactored
const isFieldDirty = (
    [key, value]: [string, number | CustomFieldsValueType],
    initialValues:
        | Omit<TicketFormValues, 'channel' | 'commentType' | 'distributionListTo'>
        | TicketFormValues['fields']
        | RequesterEditFormValues
): boolean => {
    const isNeededStringifyCheck = Array.isArray(value);

    if (isNeededStringifyCheck) return stringifyCheck(value, initialValues?.[key]);

    // prevent inconsistent result by checking empty-string for nullishValue
    if (!value) return '' !== (initialValues?.[key] ?? '');

    return (
        (dayjs.isDayjs(value) ? value.toISOString() : value) !==
        (dayjs.isDayjs(initialValues?.[key]) ? initialValues?.[key]?.toISOString() : initialValues?.[key] ?? '')
    );
};

// ! refactored
export const checkFormDirtyStatus = <
    T extends Omit<TicketFormValues, 'channel' | 'commentType' | 'distributionListTo'> | TicketFormValues['fields']
>(
    currentValues: T | RequesterEditFormValues,
    initialValues: T | RequesterEditFormValues
): boolean => {
    const isDirty = Object.entries(currentValues).reduce((acc, [key, value]) => {
        if (acc) return true;

        // recursion for fields property of form values
        if (key === FormSystemFields.name)
            return checkFormDirtyStatus(
                currentValues?.fields as TicketFormValues['fields'],
                initialValues?.fields as TicketFormValues['fields']
            );

        return isFieldDirty([key, value], initialValues);
    }, false);

    return isDirty;
};

// ! refactored
export const isTicketFormDirty = <T extends TicketFormValues>(currentFormValues: T, initialFormValues: T): boolean => {
    const currentValues = {
        activeForm: currentFormValues?.activeForm,
        comment: currentFormValues?.comment,
        fields: currentFormValues?.fields
    };

    if (Object.values(currentValues).filter(nonNullishValue).length === 0) {
        // console.debug('isTicketFormDirty: check currentFormValues, will return false');
        return false;
    }

    const initialValues = {
        activeForm: initialFormValues?.activeForm,
        comment: initialFormValues?.comment,
        fields: initialFormValues?.fields
    };

    if (Object.values(initialValues).filter(nonNullishValue).length === 0) {
        // console.debug('isTicketFormDirty: check initialFormValues, will return false');
        return false;
    }

    return checkFormDirtyStatus(currentValues, initialValues);
};
